import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { getMuscleGroupVolume } from '../../../services/muscleGroupService';
import LoadFailed from '../LoadFailed';
import { getMovementCategoryColor } from '../../../constants/exerciseCategories';
import { colors, typography, radius, spacing } from '../../../theme';

/**
 * Training Distribution — renamed from "Muscle Group Focus", backed by
 * lifted volume (getMuscleGroupVolume) rather than the old set-frequency
 * count, to match the "based on completed training volume" copy. Donut
 * chart per user request (back from the horizontal-bar version), mirroring
 * the coach's existing "Muscle Group Split" donut in ClientPerformanceTab.jsx
 * — same Recharts Pie/Cell setup and the same MOVEMENT_CATEGORY_COLORS
 * source, so a category renders identically for coach and client. Tap
 * instead of hover to select a segment (this app is mobile-first; hover
 * doesn't work on touch).
 */
const TrainingDistribution = ({ userId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoadFailed(false);
    getMuscleGroupVolume(userId, { strict: true })
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, attempt]);

  if (loading) return null;
  if (loadFailed) return <LoadFailed what="your training distribution" onRetry={() => setAttempt((n) => n + 1)} />;
  if (data.length === 0) return null;

  const active = activeIndex != null ? data[activeIndex] : null;
  const activeColor = active ? getMovementCategoryColor(active.category) : colors.text;

  return (
    <div style={styles.container}>
      <div style={{ ...typography.sectionHeading, color: colors.text }}>Training Distribution</div>
      <div style={{ ...typography.caption, color: colors.textMuted, marginTop: '2px', marginBottom: spacing.lg }}>
        An estimate of where your training goes, from your completed sets. Muscles that help in an
        exercise get part of the credit, so these are shares, not kilograms lifted.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flexShrink: 0, width: 150, height: 150 }}>
          <PieChart width={150} height={150}>
            <Pie
              data={data}
              dataKey="volume"
              nameKey="category"
              cx={75}
              cy={75}
              innerRadius={45}
              outerRadius={70}
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
            >
              {data.map((entry, index) => (
                <Cell
                  key={entry.category}
                  fill={getMovementCategoryColor(entry.category)}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.35}
                  onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </Pie>
          </PieChart>
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center', pointerEvents: 'none',
          }}>
            {active ? (
              <>
                <div style={{ color: activeColor, fontSize: '18px', fontWeight: '800', lineHeight: 1.1 }}>{active.percent}%</div>
                <div style={{ color: activeColor, fontSize: '11px', marginTop: '2px', maxWidth: '64px', lineHeight: 1.2 }}>{active.category}</div>
              </>
            ) : (
              <>
                {/* Not a kg total: credit for helping muscles made it larger than the weight lifted (task 017, M5) */}
                <div style={{ color: colors.text, fontSize: '15px', fontWeight: '700', lineHeight: 1.1 }}>{data.length}</div>
                <div style={{ color: colors.textMuted, fontSize: '11px', marginTop: '1px' }}>{data.length === 1 ? 'area' : 'areas'}</div>
              </>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: '140px', display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {data.map((item, index) => {
            const itemColor = getMovementCategoryColor(item.category);
            return (
              <div
                key={item.category}
                onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                style={{ cursor: 'pointer', opacity: activeIndex === null || activeIndex === index ? 1 : 0.5 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: itemColor, flexShrink: 0 }} />
                    <span style={{ ...typography.caption, color: colors.text }}>{item.category}</span>
                  </div>
                  <span style={{ ...typography.caption, color: colors.textMuted }}>{item.percent}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: spacing.lg,
    backgroundColor: colors.surface1,
    borderRadius: radius.card,
    border: `1px solid ${colors.border}`,
    marginBottom: spacing.lg,
  },
};

export default TrainingDistribution;
